import { useFlag, useNewCode } from '@calendly/feature-check-react';
import { useQueryParams } from '@calendly/hooks';
import { buildQuery } from '@calendly/http-tools';
import { I18n } from '@calendly/i18n';
import { Cell as TableCell, Row } from '@calendly/table';
import { Button } from '@calendly/ui/components/button';
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy';
import { AddMiniIcon, AdminIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingState from 'components/core/loading_state';
import { EMPTY_STATE_TYPES, EmptyState } from 'components/empty_state';
import { EventTypeSelectPopup } from 'components/event_type_select_popup';
import { getSingleUseLinkEventTypeAttrs, useSingleUseLinkSidebar } from 'features/single_use_link_sidebar';
import { useHomeTabs } from 'hooks/home/tabs';
import { useMeetingIntelligenceFeatureFlag } from 'hooks/meeting_intelligence_awareness';
import { ClearReservedTimesModal, ContactAvatar, CreatedAt, CreatedAtWithLabel, MainActionTableButton, SelectCell, Status, Table } from '../shared';
import { useFetchSingleUseLinkDataQuery } from './api';
import { BulkToolbar } from './bulk_actions';
import { RowActionButtonWrapper, RowDesktopNoWrap } from '../shared/styles';
import { ActionButton } from './components/action_button';
import { EmptyState as TableEmptyState } from './components/empty_state';
import { Menu } from './menu';
import { CancelMeetingModal } from '../shared/cancel_meeting_modal';
import { countNonDefaultFilters } from '../shared/filters/helpers';
const flexRowCss = "fk6j28n";
const FlexRow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexRowCss, props.className)} />;
});
const nameWrapperCss = "njex9xp";
const NameWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameWrapperCss, props.className)} />;
});
const textOverflowIsDesktopUpCss = "t1wgsi5d";
const textOverflowMaxWidthSmallCss = "t8486fm";
const textOverflowMaxWidthLargeCss = "ts0bwqi";
const textOverflowCss = "t183ywky";
const TextOverflow = forwardRef((props, ref) => {
  const {
    maxWidth,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isDesktopUp = useDeviceType('isDesktopUp');
  return <Component ref={ref} {...restProps} className={classNames(textOverflowCss, {
    [textOverflowIsDesktopUpCss]: isDesktopUp,
    [textOverflowMaxWidthSmallCss]: maxWidth?.toString() === 'small',
    [textOverflowMaxWidthLargeCss]: maxWidth?.toString() === 'large'
  }, props.className)} />;
});
const colorCircleCss = "c148ck1b";
const ColorCircle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    color,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const style = {
    backgroundColor: color
  };
  return <Component ref={ref} {...restProps} style={style} className={classNames(colorCircleCss, props.className)} />;
});
const nameColumnWrapperCss = "n3bnkes";
const NameColumnWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameColumnWrapperCss, props.className)} />;
});
const quarterCircleCss = "qbwrram";
const QuarterCircle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(quarterCircleCss, props.className)} />;
});
const cellIsTabletDownCss = "chemyf2";
const cellPaddingLeftCss = "c18vb9xf";
const cellCss = "chit7u2";
const Cell = forwardRef((props, ref) => {
  const {
    paddingLeft,
    ...restProps
  } = props;
  const isTabletDown = useDeviceType('isTabletDown');
  return <TableCell ref={ref} {...restProps} className={classNames(cellCss, {
    [cellIsTabletDownCss]: isTabletDown,
    [cellPaddingLeftCss]: paddingLeft
  }, props.className)} />;
});
const SingleUseLinkRow = ({
  singleUseLink,
  ...props
}) => {
  const history = useHistory();
  const {
    getTabScopeV2
  } = useHomeTabs();
  const {
    type: scopeType,
    id: scopeId
  } = getTabScopeV2();
  const {
    openSidebar
  } = useSingleUseLinkSidebar();
  const isMIEnabled = useMeetingIntelligenceFeatureFlag();
  const hasNewMeetingsPage = useFlag('sa_new_meetings_page', false);
  const isNewMeetingsPageEnabled = isMIEnabled || hasNewMeetingsPage;
  const openEvent = useCallback(event => {
    const params = {
      expand: true,
      period: event.period,
      uuid: event.uuid
    };
    const path = isNewMeetingsPageEnabled ? `/app/meetings/${scopeType}/${scopeId}?selected=${event.uuid}` : `/app/scheduled_events/${scopeType}/${scopeId}${buildQuery(params, {
      addQueryPrefix: true
    })}`;
    history.push(path);
  }, [history, isNewMeetingsPageEnabled, scopeId, scopeType]);
  const handleClick = useCallback(() => {
    if (singleUseLink.status === 'booked') {
      openEvent(singleUseLink.event);
    } else {
      const selectedEventTypeAttrs = getSingleUseLinkEventTypeAttrs(get(singleUseLink, 'share.eventType', null));
      openSidebar({
        selectedEventTypeAttrs,
        singleUseLinkAttrs: {
          shareUuid: singleUseLink.share.uuid
        }
      });
    }
  }, [openEvent, openSidebar, singleUseLink]);
  return <Row onClick={handleClick} {...props} />;
};
const NameColumn = ({
  name,
  shareOverrideName,
  etColor,
  isEtManaged,
  isMobile,
  isAdHoc
}) => <NameColumnWrapper>
    {!isAdHoc && <FlexRow>
        <QuarterCircle />
        <TextOverflow maxWidth="large">
          {name}
        </TextOverflow>
      </FlexRow>}
    <NameWrapper>
      {etColor && <ColorCircle color={etColor} />}
      <TextOverflow maxWidth="small">
        {shareOverrideName || name}
        {isEtManaged && isMobile && <AdminIcon spacingLeft="4" inline />}
      </TextOverflow>
      {isEtManaged && !isMobile && <AdminIcon spacingLeft="4" inline />}
    </NameWrapper>
  </NameColumnWrapper>;
const isAdHocET = singleUseLink => singleUseLink.share.eventType.type === 'adhoc';
const isSelectable = singleUseLink => !(isAdHocET(singleUseLink) && singleUseLink.status === 'booked') && singleUseLink.policy.cancel;
export const List = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const openedUuidRef = useRef(null);
  const [currentEventTypeId, setCurrentEventTypeId] = useState(null);
  const [pendingShare, setPendingShare] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const {
    getTabScopeV2
  } = useHomeTabs();
  const {
    type: scopeType,
    id: scopeId
  } = getTabScopeV2();
  const {
    pane: _pane,
    paneState: _paneState,
    ...queryParams
  } = useQueryParams();
  const isDesktopUp = useDeviceType('isDesktopUp');
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useFetchSingleUseLinkDataQuery({
    scopeType,
    scopeId,
    queryParams
  });
  const {
    openSidebar: openSingleUseLinkSidebar
  } = useSingleUseLinkSidebar();
  const singleUseLinksData = data?.results;
  const onlyMyScopeSelected = scopeType === 'user' && scopeId === 'me';
  const hasActiveFilters = !!queryParams.filter_term || countNonDefaultFilters(queryParams) > 0;
  const isEmptyState = isError || isFetching || singleUseLinksData?.length === 0;
  const hideSentToColumn = true;
  const isSingleUseLinkEmptyState = useNewCode('qol_sul_empty_state', false)?.enabled;
  const [isEventTypeSelectPopupOpen, setIsEventTypeSelectPopupOpen] = useState(false);
  const columns = useMemo(() => [I18n.t('publisher.scheduling.shared.column_headers.name'), I18n.t('publisher.scheduling.shared.column_headers.created'), I18n.t('publisher.scheduling.shared.column_headers.status'), ...(!hideSentToColumn ? [I18n.t('publisher.scheduling.single_use_links.column_headers.sent_to')] : [])], [hideSentToColumn]);
  const createSingleUseLink = useCallback(eventType => {
    const eventTypeAttrs = getSingleUseLinkEventTypeAttrs(eventType);
    openSingleUseLinkSidebar({
      selectedEventTypeAttrs: eventTypeAttrs,
      isCreation: true
    });
  }, [openSingleUseLinkSidebar]);
  const onEventTypeSelect = eventType => {
    setIsEventTypeSelectPopupOpen(false);
    createSingleUseLink(eventType);
  };
  const triggerClearReservedTimesUuid = useMemo(() => queryParams.trigger_clear_reserved_times, [queryParams.trigger_clear_reserved_times]);
  const triggerDeleteUuid = useMemo(() => queryParams.trigger_delete, [queryParams.trigger_delete]);
  const queryParamsWithoutTrigger = useMemo(() => {
    const params = {
      ...queryParams
    };
    delete params.trigger_clear_reserved_times;
    delete params.trigger_delete;
    delete params.uuid;
    return params;
  }, [queryParams]);
  const resetURLParams = useCallback(() => {
    setTimeout(() => {
      const newSearch = new URLSearchParams(queryParamsWithoutTrigger).toString();
      if (location.search !== newSearch) {
        history.replace({
          pathname: location.pathname,
          search: newSearch
        });
      }
    }, 0);
  }, [queryParamsWithoutTrigger, location.search, location.pathname, history]);
  useEffect(() => {
    if (isLoading || !triggerClearReservedTimesUuid || !singleUseLinksData) return;
    if (openedUuidRef.current === triggerClearReservedTimesUuid) return;
    const singleUseLink = singleUseLinksData.find(link => link.share.uuid === triggerClearReservedTimesUuid);
    if (!singleUseLink) return;
    openedUuidRef.current = triggerClearReservedTimesUuid;
    setCurrentEventTypeId(singleUseLink.share.eventType.id);
    const selectedEventTypeAttrs = getSingleUseLinkEventTypeAttrs(get(singleUseLink, 'share.eventType', null));
    openSingleUseLinkSidebar({
      selectedEventTypeAttrs,
      singleUseLinkAttrs: {
        shareUuid: singleUseLink.share.uuid
      }
    });
    resetURLParams();
  }, [triggerClearReservedTimesUuid, isLoading, singleUseLinksData, openSingleUseLinkSidebar, resetURLParams]);
  useEffect(() => {
    if (isLoading || !triggerDeleteUuid || !singleUseLinksData) return;
    if (openedUuidRef.current === triggerDeleteUuid) return;
    const singleUseLink = singleUseLinksData.find(link => link.share.uuid === triggerDeleteUuid);
    if (!singleUseLink) return;
    openedUuidRef.current = triggerDeleteUuid;
    setPendingShare({
      id: singleUseLink.id,
      name: singleUseLink.share.name
    });
    openSingleUseLinkSidebar({
      singleUseLinkAttrs: {
        shareUuid: singleUseLink.share.uuid
      },
      selectedEventTypeAttrs: getSingleUseLinkEventTypeAttrs(singleUseLink.share.eventType)
    });
    resetURLParams();
  }, [triggerDeleteUuid, isLoading, singleUseLinksData, openSingleUseLinkSidebar, resetURLParams]);
  const createSingleUseLinkButton = ({
    buttonLabel
  }) => <Button onClick={() => setIsEventTypeSelectPopupOpen(true)}>
      <AddMiniIcon />
      {buttonLabel}
    </Button>;
  useEffect(() => () => {
    openedUuidRef.current = null;
    setCurrentEventTypeId(null);
  }, []);
  if (isLoading) {
    return <LoadingState />;
  }
  if (!isError && onlyMyScopeSelected && !hasActiveFilters && singleUseLinksData.length === 0) {
    return <>
        <EmptyState type={EMPTY_STATE_TYPES.leftRight} contentKey="single_use_links" featureKey="single_use_links" hideActionButton={!isSingleUseLinkEmptyState} buttonOverride={createSingleUseLinkButton} />
        <EventTypeSelectPopup isOpen={isEventTypeSelectPopupOpen} onCancel={() => setIsEventTypeSelectPopupOpen(false)} onEventTypeSelect={onEventTypeSelect} defaultValue={null} actionKind="single_use_link_creation" />
      </>;
  }
  return <>
      <Table columns={columns} setSelectedRowsState={state => state} selectedRows={selectedRows} selectableRows={singleUseLinksData?.filter(link => isSelectable(link))} setSelectedRows={setSelectedRows} events={singleUseLinksData} pagination={data?.pagination}>
        {isEmptyState ? <TableEmptyState isError={isError} isLoading={isFetching} refetch={refetch} /> : singleUseLinksData.map(singleUseLink => <SingleUseLinkRow singleUseLink={singleUseLink} key={singleUseLink.id}>
              <Cell>
                <SelectCell selectedRows={selectedRows} setSelectedRows={setSelectedRows} disabled={!isSelectable(singleUseLink)} rowId={singleUseLink.id} aria-label={I18n.t('publisher.components.table.select_item', {
            item: singleUseLink.share.shareOverride?.name || singleUseLink.share.eventType.name
          })} />
              </Cell>
              <Cell>
                <MainActionTableButton>
                  <NameColumn name={singleUseLink.share.eventType.name} shareOverrideName={singleUseLink.share.shareOverride?.name} etColor={singleUseLink.share.eventType.color} isEtManaged={singleUseLink.share.eventType.isManaged} isAdHoc={isAdHocET(singleUseLink)} isMobile={!isDesktopUp} />
                </MainActionTableButton>
              </Cell>
              <Cell paddingLeft>
                {isDesktopUp ? <RowDesktopNoWrap>
                    <CreatedAt createdAt={singleUseLink.share.createdAt} />
                  </RowDesktopNoWrap> : <CreatedAtWithLabel createdAt={singleUseLink.share.createdAt} />}
              </Cell>
              <Cell paddingLeft noWrap>
                <RowDesktopNoWrap>
                  <Status status={singleUseLink.status} />
                </RowDesktopNoWrap>
              </Cell>
              {!hideSentToColumn && <Cell paddingLeft>
                  <FullStoryExclude>
                    <ContactAvatar contact={singleUseLink.contact} />
                  </FullStoryExclude>
                </Cell>}
              <Cell align="right" hideOnMobile noWrap>
                <RowActionButtonWrapper>
                  <ActionButton singleUseLink={singleUseLink} />
                </RowActionButtonWrapper>
              </Cell>
              <Cell align="right" hasActions>
                <Menu singleUseLink={singleUseLink} fullActionsList={!isDesktopUp} />
              </Cell>
            </SingleUseLinkRow>)}
      </Table>
      <ClearReservedTimesModal triggerEventUUID={currentEventTypeId} />
      <CancelMeetingModal link={pendingShare} onClose={() => setPendingShare(null)} />
      <BulkToolbar selectedSingleUseLinks={(singleUseLinksData ?? []).filter(link => selectedRows.includes(link.id))} deselectAll={() => setSelectedRows([])} />
    </>;
};

require("./list.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./list.js");